import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { HighlightModule } from '@app/shared';
import { FilterWidgetModule } from '@app/shared/components/filter-widget/filter-widget.module';
import { PaginatorComponent } from '@app/shared/components/paginator/paginator.component';
import { PaginatorModule } from '@app/shared/components/paginator/paginator.module';
import { SearchWidgetComponent } from '@app/shared/components/search-widget/search-widget.component';
import { SearchWidgetModule } from '@app/shared/components/search-widget/search-widget.module';
import { SortWidgetModule } from '@app/shared/components/sort-widget/sort-widget.module';
import { ResponsiveTableComponent } from '@app/shared/components/table/responsive-table.component';
import { ResponsiveTableModule } from '@app/shared/components/table/responsive-table.module';
import { BadgeComponent, ToastService, ToolbarComponent } from '@teorema/patron';
import {
  FilterField,
  FindOptions,
  PaymentLink,
  PaymentLinkStatus,
  PaymentLinkType,
} from 'src/app/models';
import { PaymentLinkService } from 'src/app/shared/services';
import { PaymentLinkStatusBadgeComponent } from '../status-badge/status-badge.component';

@Component({
  selector: 'payment-links-page',
  templateUrl: './payment-links-page.component.html',
  imports: [
    CommonModule,
    ToolbarComponent,
    SortWidgetModule,
    FilterWidgetModule,
    SearchWidgetModule,
    PaginatorModule,
    ResponsiveTableModule,
    HighlightModule,
    PaymentLinkStatusBadgeComponent,
    BadgeComponent,
  ],
})
export class PaymentLinksPageComponent {
  entities: PaymentLink[] = [];
  loading: boolean = false;
  totalItems = 0;
  findOptions: FindOptions = {
    storageKey: 'payment-links-find-options',
    orderBy: [{ field: 'created_at', direction: 'desc' }],
    q: '',
    fields: [
      {
        label: 'Data',
        columnWidth: 170,
      },
      {
        label: 'Link',
      },
      {
        label: 'Cliente',
      },
      {
        label: 'Ref doc',
        columnWidth: 180,
      },
      {
        label: 'Valor',
        columnWidth: 130,
      },
      {
        label: 'Status',
        columnWidth: 100,
        columnClass: 'text-right',
      },
    ],
    filters: [
      new FilterField({
        label: 'Status',
        name: 'status',
        type: 'select',
        multiple: true,
        options: [
          { label: 'Aberto', value: PaymentLinkStatus.ABERTO },
          { label: 'Aprovado', value: PaymentLinkStatus.APPROVED },
          { label: 'Pendente', value: PaymentLinkStatus.PENDING },
          { label: 'Cancelado', value: PaymentLinkStatus.CANCELLED },
          { label: 'Rejeitado', value: PaymentLinkStatus.REJECTED },
        ],
      }),
    ],
  };

  @ViewChild('paginator') paginator!: PaginatorComponent;
  @ViewChild('searchWidget') searchWidget!: SearchWidgetComponent;
  @ViewChild('table') table!: ResponsiveTableComponent;

  private service = inject(PaymentLinkService);
  private toastService = inject(ToastService);

  async find() {
    this.loading = true;
    const response = await this.service.findAll(this.findOptions).catch(() => {
      this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
    });

    if (response?.data) {
      this.entities = response.data;
    }

    if (response?.meta?.total != null && response?.meta?.total >= 0) {
      this.totalItems = response.meta.total;
    }

    this.loading = false;
  }

  async paginate(findOptions?: FindOptions) {
    if (findOptions) this.findOptions = { ...this.findOptions, ...findOptions };

    if (this.findOptions?.q) {
      this.searchWidget.updateValue(this.findOptions.q);
    }

    await this.find();
  }

  onSearch(searchText?: string) {
    if (searchText != this.findOptions.q) {
      this.findOptions.q = searchText;
      this.goToFirstPage();
    }
  }

  goToFirstPage() {
    this.paginate({ page: 1 });
    this.paginator.component.first = 0;
  }

  getType(type: PaymentLinkType) {
    if (type == PaymentLinkType.ORDER) return 'Pedido';
    return type;
  }

  isExpired(expiration: Date) {
    return new Date() > expiration;
  }
}
